// src/features/etf-ranking/components/CategoryRadar.tsx
//
// Bond-category factor radar. Each spoke is a fixed-income category
// (Treasury_Long, Corporate_HY, ...); the three overlaid series are the
// category-average Momentum / Value / Sentiment z-scores returned by
// GET /api/v1/etf-ranking/category-summary. This lets a user see, at a
// glance, which factor is "lit up" in which corner of the bond market
// (e.g. long Treasuries strong on Value, high-yield strong on Momentum).
// Fixed-income sibling of features/stock-ranking/components/SectorRadar.

import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import type { CategorySummaryRow } from '../api';

interface CategoryRadarProps {
    categories: CategorySummaryRow[];
}

// Recharts wants one object per spoke with a key per series.
interface RadarDatum {
    category: string;
    Momentum: number;
    Value: number;
    Sentiment: number;
}

// Category buckets are stored as snake_case tokens (Treasury_Long); make
// them human-readable for the angle-axis labels so the radar stays
// legible. Falls back to the de-underscored token when unmapped.
const prettify = (category: string): string => {
    const map: Record<string, string> = {
        Treasury_Short: 'Treas. Short',
        Treasury_Intermediate: 'Treas. Int.',
        Treasury_Long: 'Treas. Long',
        Corporate_IG: 'Corp. IG',
        Corporate_HY: 'Corp. HY',
        Municipal: 'Muni',
        Aggregate: 'Aggregate',
        International: 'Intl',
        EmergingMarket: 'EM',
        TIPS: 'TIPS',
        MBS: 'MBS',
    };
    return map[category] ?? category.replace(/_/g, ' ');
};

const RadarTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: <span className="font-mono">{p.value.toFixed(2)}</span>
                </p>
            ))}
        </div>
    );
};

export const CategoryRadar = ({ categories }: CategoryRadarProps) => {
    if (!categories || categories.length === 0) {
        return (
            <p className="muted text-sm">
                No category aggregates available yet.
            </p>
        );
    }

    const data: RadarDatum[] = categories.map((c) => ({
        category: prettify(c.category),
        Momentum: Number(c.avg_momentum_z.toFixed(3)),
        Value: Number(c.avg_value_z.toFixed(3)),
        Sentiment: Number(c.avg_sentiment_z.toFixed(3)),
    }));

    return (
        <div
            className="w-full h-96 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Average factor z-scores by bond category"
        >
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data} outerRadius="72%">
                    <PolarGrid className="stroke-slate-200 dark:stroke-slate-700" />
                    <PolarAngleAxis
                        dataKey="category"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        tick={{ fontSize: 10, fill: 'currentColor' }}
                    />
                    <Radar
                        name="Momentum"
                        dataKey="Momentum"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Value"
                        dataKey="Value"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Sentiment"
                        dataKey="Sentiment"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.15}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip content={<RadarTooltip />} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CategoryRadar;
